import { fetchAuthSession } from 'aws-amplify/auth';
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { isSuperAdminRole, matchesRoleAllowList } from '@/utils/roles';
import { RootState } from '../redux/store';
import { setUserRole, clearUser } from '../redux/userSlice';
import FallbackSpinner from './FallbackSpinner';

interface RoleBasedRouteProps {
  allowedRoles: string[];
}

const RoleBasedRoute: React.FC<RoleBasedRouteProps> = ({ allowedRoles }) => {
  const userRole = useSelector((state: RootState) => state.user.role);
  const dispatch = useDispatch();
  const location = useLocation();
  const [isCheckingAuth, setIsCheckingAuth] = useState(!userRole);
  useEffect(() => {
    if (userRole) {
      setIsCheckingAuth(false);
      return;
    }

    let isMounted = true;

    const checkAuthAndRole = async () => {
      try {
        const session = await fetchAuthSession();
        if (!isMounted) return;

        if (!session?.tokens?.idToken) {
          dispatch(clearUser());
          setIsCheckingAuth(false);
          return;
        }

        const payload = session.tokens.idToken.payload;
        const tokenRole =
          typeof payload?.['custom:role'] === 'string' ? payload['custom:role'] : undefined;
        if (tokenRole) {
          dispatch(setUserRole(tokenRole));
        }
        setIsCheckingAuth(false);
      } catch (_error) {
        if (isMounted) {
          setIsCheckingAuth(false);
        }
      }
    };

    checkAuthAndRole();

    return () => {
      isMounted = false;
    };
  }, [userRole, dispatch]);

  if (isCheckingAuth) {
    return <FallbackSpinner />;
  }

  if (!userRole) {
    return <Navigate to="/member/login" state={{ from: location }} replace />;
  }

  if (isSuperAdminRole(userRole) || matchesRoleAllowList(userRole, allowedRoles)) {
    return <Outlet />;
  }

  return <Navigate to="/unauthorized" state={{ from: location }} replace />;
};

export default RoleBasedRoute;
